import { useEffect, useState } from 'react';
import {
  PhoneIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Avatar from '../../components/Avatar';
import { useSocketEvent } from '../../lib/websocket/hooks';
import { SOCKET_EVENTS, type AiEscalationPayload } from '../../lib/websocket/events';
import { useVoiceStore } from '../../stores/voiceStore';
import { twilioClient } from './twilio-client';
import { notificationSound } from '../../lib/notificationSound';

/**
 * V2 AI voice agent escalation alert.
 *
 * The voice agent ends a call when it can't fully handle the customer's
 * request (currently: anything requiring staff judgement) and pings every
 * online staff dashboard with this toast. One-click "Call back now"
 * dials the customer immediately via the existing Twilio outbound flow,
 * closing the gap between the AI hanging up and staff actually getting
 * back to the customer.
 *
 * Behavior:
 *   - Auto-dismiss after 2 min so it doesn't stack if multiple come in.
 *   - First staff who calls back claims the conversation (server-side
 *     state is `awaiting_staff` until then).
 *   - Plays the urgent notification chime once.
 *   - Surfaces the AI-provided reason so staff knows what the customer
 *     wanted without opening the conversation first.
 */
const AUTO_DISMISS_MS = 120_000;

interface ActiveEscalation extends AiEscalationPayload {
  receivedAt: number;
}

export default function AiEscalationToast() {
  const [active, setActive] = useState<ActiveEscalation | null>(null);
  const [calling, setCalling] = useState(false);
  const navigate = useNavigate();
  const activeCall = useVoiceStore((s) => s.activeCall);
  const setActiveCall = useVoiceStore((s) => s.setActive);

  useSocketEvent(SOCKET_EVENTS.VOICE_AI_ESCALATION, (payload: AiEscalationPayload) => {
    setActive({ ...payload, receivedAt: Date.now() });
    try {
      notificationSound.playUrgent();
    } catch {
      /* user gesture required */
    }
  });

  useEffect(() => {
    if (!active) return;
    const handle = window.setTimeout(() => setActive(null), AUTO_DISMISS_MS);
    return () => window.clearTimeout(handle);
  }, [active]);

  if (!active) return null;

  const handleCallBack = async () => {
    if (calling) return;
    if (activeCall) {
      toast.error('End your current call first');
      return;
    }
    if (twilioClient.getDevice()?.state !== 'registered') {
      toast.error(
        'Voice not ready — turn on "Available for calls" and reload, then try again',
      );
      return;
    }

    setCalling(true);
    try {
      const call = await twilioClient.startOutboundCall(
        active.customerPhone,
        active.conversationId,
      );
      if (!call) {
        toast.error('Could not place call');
        return;
      }
      setActiveCall({
        call: {
          id: 'outbound',
          conversationId: active.conversationId,
          customerName: active.customerName,
          customerPhone: active.customerPhone,
          transport: 'twilio',
          ringingAt: new Date().toISOString(),
          offerSdp: '',
        },
        transport: 'twilio',
        twilioCall: call,
        answeredAt: new Date().toISOString(),
      });
      setActive(null);
    } catch (err) {
      console.warn('[ai-escalation] callback failed:', err);
      toast.error('Could not place call');
    } finally {
      setCalling(false);
    }
  };

  const handleOpenConversation = () => {
    navigate(`/conversations/${active.conversationId}`);
    setActive(null);
  };

  const displayName = active.customerName || active.customerPhone || 'Customer';

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[70] w-96 max-w-[calc(100vw-2rem)] animate-slide-in">
      <div className="pointer-events-auto overflow-hidden rounded-card border border-warning/40 bg-surface shadow-l">
        <div className="flex items-center justify-between border-b border-line bg-warning/10 px-4 py-2">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-warning">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-warning" />
            </span>
            🤖 AI handoff — call customer back
          </div>
          <button
            onClick={() => setActive(null)}
            className="rounded p-1 text-ink-3 hover:bg-black/[0.04] hover:text-ink"
            aria-label="Dismiss"
            type="button"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-start gap-3 px-4 py-3">
          <Avatar name={displayName} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{displayName}</p>
            <p className="truncate font-mono text-xs text-ink-2">
              {active.customerPhone}
            </p>
            {active.reason && (
              <p className="mt-1 text-xs leading-snug text-ink-2">
                <span className="font-medium text-ink-3">Reason:</span>{' '}
                {active.reason}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2 border-t border-line bg-surface-2 px-4 py-3">
          <button
            onClick={handleOpenConversation}
            type="button"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-surface border border-line px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-black/[0.04]"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            Open
          </button>
          <button
            onClick={handleCallBack}
            disabled={calling}
            type="button"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-brand px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PhoneIcon className="h-4 w-4" />
            {calling ? 'Calling…' : 'Call back now'}
          </button>
        </div>
      </div>
    </div>
  );
}
